import store from '../store/index'
import axios from '../axios/main'
import Types from '../store/types'
import LocalDatabase from './LocalDatabase'

// Token refresh management
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

axios.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config

    // Handle network errors or missing response
    if (!error.response) {
      return Promise.reject(error)
    }

    const { status } = error.response

    // Only handle 401 errors
    if (status !== 401) {
      return Promise.reject(error)
    }

    // If refresh token endpoint itself failed, logout user
    if (originalRequest.url && originalRequest.url.includes('/refresh-token')) {
      console.log('Token refresh failed, logging out user')
      await store.dispatch(Types.actions.UPDATE_AUTH_RESET)
      window.location.href = '/'
      return Promise.reject(error)
    }

    // Prevent infinite retry loops
    if (originalRequest._retry) {
      await store.dispatch(Types.actions.UPDATE_AUTH_RESET)
      window.location.href = '/'
      return Promise.reject(error)
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise(function(resolve, reject) {
        failedQueue.push({ resolve, reject })
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return axios(originalRequest)
        })
        .catch(err => {
          return Promise.reject(err)
        })
    }

    // Mark request as retried
    originalRequest._retry = true
    isRefreshing = true

    // Attempt to refresh the token
    try {
      const token = await LocalDatabase.get(Types.localdb.USER_TOKEN)

      if (!token) {
        throw new Error('No token found')
      }

      console.log('Token expired, refreshing...')

      // Call refresh-token endpoint
      const response = await axios.post('/api/refresh-token', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data && response.data.access_token) {
        const newToken = response.data.access_token

        // Save new token
        await LocalDatabase.set(Types.localdb.USER_TOKEN, newToken)
        await store.dispatch(Types.actions.UPDATE_USER_TOKEN, newToken)

        // Update axios default headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        console.log('Token refreshed successfully')

        // Process queued requests
        processQueue(null, newToken)

        // Retry original request with new token
        return axios(originalRequest)
      } else {
        throw new Error('No access_token in refresh response')
      }
    } catch (refreshError) {
      console.error('Token refresh error:', refreshError)
      processQueue(refreshError, null)

      // Logout user on refresh failure
      await store.dispatch(Types.actions.UPDATE_AUTH_RESET)
      window.location.href = '/'

      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

axios.interceptors.request.use(
  async function(config) {
    const token = await LocalDatabase.get(Types.localdb.USER_TOKEN)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  function(error) {
    // Do something with request error
    return Promise.reject(error)
  }
)
